import Vue from "vue";
import VueI18n from "vue-i18n";
import store from "@/store";
import moment from "moment-timezone";

Vue.use(VueI18n);

const fallbackLocale = "da";
const availableLanguages = JSON.parse(process.env.VUE_APP_AVAILABLE_LANGUAGES);
const loadedLanguages = [];

function setI18nLanguage(lang) {
    i18nInstance().locale = lang;
    moment.locale(lang);
    document.querySelector("html").setAttribute("lang", lang);
    store.commit("date/forceDateUpdate");
    return lang;
}

function loadLanguage(lang, langModulePromise) {
    return langModulePromise
        .then(langModule => {
            return langModule.default;
        })
        .then(messages => {
            i18nInstance().setLocaleMessage(lang, messages);
            loadedLanguages.push(lang);
            return setI18nLanguage(lang);
        });
}

function navigatorLocale() {
    try {
        const locale = (navigator.language || navigator.userLanguage).split(
            "-"
        )[0];

        if (locale) {
            if (availableLanguages.includes(locale)) {
                return locale;
            } else if (availableLanguages.includes("en")) {
                return "en";
            } else {
                return null;
            }
        } else {
            return null;
        }
    } catch (e) {
        return null;
    }
}

function cookieLocale() {
    try {
        const locale = Vue.$cookies.get("locale");
        return availableLanguages.includes(locale) ? locale : null;
    } catch (e) {
        return null;
    }
}

function saveLocale(locale) {
    Vue.$cookies.set("locale", locale, Infinity);
}

function defaultLocale() {
    const localeFromCookie = cookieLocale();
    const localeFromNavigator = navigatorLocale();
    return localeFromCookie || localeFromNavigator || fallbackLocale;
}

let instance;
export const i18nInstance = () => {
    if (!instance) {
        instance = new VueI18n({
            locale: defaultLocale(),
            fallbackLocale,
            messages: {}
        });
    }
    return instance;
};

export function loadLanguageAsync(lang, storeInCookie = false) {
    if (!availableLanguages.includes(lang)) {
        return Promise.resolve(i18nInstance().locale);
    }

    if (storeInCookie) {
        try {
            saveLocale(lang);
        } catch (e) {
            console.error(e);
        }
    }

    if (i18nInstance().locale === lang) {
        return Promise.resolve(lang);
    }

    if (loadedLanguages.includes(lang)) {
        return Promise.resolve(setI18nLanguage(lang));
    }

    // Import is a Webpack function which can dynamically determine which files to load, and include in a bundle.
    // Chunkname is used to specify what the language file will be called when build occurs
    // For this to work it is important that the folder importet has no children or unrelated files in it
    // Otherwise webpack will have to bundle them all which is a waste

    // We use [request] which import/dynamic-import-chunkname does not support
    // eslint-disable-next-line import/dynamic-import-chunkname
    return loadLanguage(
        lang,
        import(
            /* webpackChunkName: "lang-[request]", webpackMode: "lazy" */ `@/i18n/languages/${lang}`
        )
    );
}

export default function() {
    // Eager load danish as the default language, no chunk should be created
    const locale = i18nInstance().locale;

    return loadLanguage(
        locale,
        import(
            /* webpackChunkName: "lang-default", webpackMode: "eager" */ `@/i18n/languages/${locale}`
        )
    );
}
